<template>
  <div class="step-section">
    <div class="step-container">
      <h2 class="step-title">
        {{ $store.state.bookingSteps.stepsData.step2.title }}
      </h2>

      <div v-if="loading" class="loading-grid">
        <div v-for="index in 12" :key="index" class="skeleton-item">
          <div class="skeleton-loader"></div>
        </div>
      </div>

      <div v-else-if="doctors.length" class="doctors-grid">
        <div
          v-for="(doctor, index) in doctors"
          :key="doctor.id"
          class="doctor-card"
          :class="{ 'doctor-card--active': selected === index }"
          @click="selectDoctor(index)"
        >
          <div class="doctor-details">
            <img
              :src="doctor.image_url"
              :alt="doctor.name"
              class="doctor-image"
            />
            <div class="doctor-copy">
              <h5>{{ doctor.name }}</h5>
              <h6>{{ selectedSpeciality }}</h6>
            </div>
          </div>

          <button
            class="doctor-action"
            type="button"
            @click.stop="viewSlots(index)"
          >
            {{ $t("View available slots") }}
            <v-icon small>{{ forwardIcon }}</v-icon>
          </button>
        </div>
      </div>

      <div v-else class="empty-state">
        <p>{{ $t("No doctors available") }}</p>
      </div>
    </div>

    <steps-nav
      :cancel-button-label="
        $store.state.bookingSteps.stepsData.cancelButtonLabel
      "
      :next-button-label="$store.state.bookingSteps.stepsData.nextButtonLabel"
      :can-proceed="selected !== null"
      @cancel="$emit('cancel')"
      @next="nextStep"
    />
  </div>
</template>

<script>
import API from "@/api/dynamicAPI";
import StepsNav from "../StepsNav";
export default {
  name: "step1",
  components: {
    StepsNav,
  },
  data() {
    return {
      selected: null,
      doctors: [],
      loading: false,
    };
  },
  async mounted() {
    this.loading = true;
    try {
      const extraAttributes = {
        specialty_id: this.$store.state.bookingSteps.selectedStepsData[
          this.$store.state.bookingSteps.stepsData.step1.selectedIdAttribute
        ],
      };

      if (
        this.$store.state.bookingSteps.stepsData.step2.request.type === "get"
      ) {
        const res = await API.getAPI(
          this.$store.state.bookingSteps.stepsData.step2.request,
          this.$store.state.bookingSteps.stepsData.step2.request.headers,
          extraAttributes
        );
        this.doctors = res && res.data ? res.data.data : [];
      } else if (
        this.$store.state.bookingSteps.stepsData.step2.request.type === "post"
      ) {
        const res = await API.postAPI(
          this.$store.state.bookingSteps.stepsData.step2.request,
          this.$store.state.bookingSteps.stepsData.step2.request.headers,
          extraAttributes
        );
        this.doctors = res && res.data ? res.data.data : [];
      }

      await this.$store.dispatch("setDoctors", this.doctors);
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    selectDoctor(index) {
      this.selected = index;
    },
    viewSlots(index) {
      this.selected = index;
      this.nextStep();
    },
    nextStep() {
      if (this.selected === null || !this.doctors[this.selected]) return;

      this.$store.state.bookingSteps.selectedStepsData[
        this.$store.state.bookingSteps.stepsData.step2.selectedNameAttribute
      ] = this.doctors[this.selected].name;
      this.$store.state.bookingSteps.selectedStepsData[
        this.$store.state.bookingSteps.stepsData.step2.selectedIdAttribute
      ] = this.doctors[this.selected].id;
      this.$store.state.bookingSteps.selectedStepsData[
        this.$store.state.bookingSteps.stepsData.step2.selectedCodeAttribute
      ] = this.doctors[this.selected].clinic_code;
      this.$store.dispatch("setSelectedDoctor", this.doctors[this.selected]);
      this.$emit("nextStep");
    },
  },
  computed: {
    selectedSpeciality() {
      return this.$store.state.bookingSteps.selectedStepsData[
        this.$store.state.bookingSteps.stepsData.step1.selectedNameAttribute
      ];
    },
    forwardIcon() {
      return this.$i18n.locale === "ar" ? "mdi-arrow-left" : "mdi-arrow-right";
    },
  },
};
</script>

<style lang="scss" scoped>
.step-section {
  padding-bottom: 12px;
}

.step-container {
  padding: 8px 28px 0;
}

.step-title {
  margin-bottom: 18px;
  color: var(--main-color);
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.skeleton-loader {
  height: 164px;
  border-radius: 24px;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite linear;
}

.doctors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.doctor-card {
  display: flex;
  flex-direction: column;
  min-height: 164px;
  padding: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  transition: all 0.22s ease;
  cursor: pointer;
}

.doctor-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.08);
}

.doctor-details {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
}

.doctor-image {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  object-fit: cover;
  background: #f8fafc;
}

.doctor-copy h5 {
  margin: 0 0 8px;
  color: var(--main-color);
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  text-transform: uppercase;
}

.doctor-copy h6 {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.doctor-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: 18px;
  border: none;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  background: transparent;
  color: var(--main-color);
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.doctor-card--active {
  color: #fff;
  background: linear-gradient(
    180deg,
    var(--main-color),
    var(--secondary-color)
  );
  border-color: transparent;
}

.doctor-card--active .doctor-copy h5,
.doctor-card--active .doctor-copy h6,
.doctor-card--active .doctor-action {
  color: #fff;
}

.doctor-card--active .doctor-action {
  border-top-color: rgba(255, 255, 255, 0.22);
}

.empty-state {
  padding: 56px 16px 64px;
  text-align: center;
  color: #64748b;
  font-size: 16px;
  font-weight: 600;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 959px) {
  .step-container {
    padding: 8px 16px 0;
  }

  .step-title {
    font-size: 20px;
  }

  .doctors-grid,
  .loading-grid {
    grid-template-columns: 1fr;
  }
}
</style>
